import { useState, useEffect } from 'react'
import { Bot, Plus, Settings, Play, Pause, Trash2, Edit, MoreVertical } from 'lucide-react'
import Card from '../../components/cards/Card'
import Button from '../../components/buttons/Button'

const Bots = () => {
  const [bots, setBots] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Simulate loading bots
    const timer = setTimeout(() => {
      setBots([
        {
          id: 1,
          name: 'Customer Support Bot',
          description: 'Handles general customer inquiries and support tickets',
          status: 'active',
          conversations: 1247,
          lastActive: '2 hours ago',
          createdAt: '2024-01-15'
        },
        {
          id: 2,
          name: 'Sales Assistant',
          description: 'Helps with product information and sales inquiries',
          status: 'inactive',
          conversations: 892,
          lastActive: '1 day ago',
          createdAt: '2024-01-10'
        },
        {
          id: 3,
          name: 'Technical Support',
          description: 'Provides technical assistance and troubleshooting',
          status: 'active',
          conversations: 567,
          lastActive: '30 minutes ago',
          createdAt: '2024-01-20'
        }
      ])
      setIsLoading(false)
    }, 1000)

    return () => clearTimeout(timer)
  }, [])

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400 bg-green-400/20'
      case 'inactive':
        return 'text-gray-400 bg-gray-400/20'
      case 'training':
        return 'text-yellow-400 bg-yellow-400/20'
      default:
        return 'text-gray-400 bg-gray-400/20'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <Play className="w-3 h-3" />
      case 'inactive':
        return <Pause className="w-3 h-3" />
      case 'training':
        return <Settings className="w-3 h-3" />
      default:
        return <Pause className="w-3 h-3" />
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-secondary-700 rounded w-64 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-secondary-800 rounded-lg p-6">
                  <div className="h-6 bg-secondary-700 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-secondary-700 rounded w-full mb-2"></div>
                  <div className="h-4 bg-secondary-700 rounded w-2/3 mb-4"></div>
                  <div className="h-8 bg-secondary-700 rounded w-full"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">AI Bots</h1>
            <p className="text-secondary-400">
              Manage and configure your AI chatbot assistants
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-secondary-400 text-sm">Total Bots</p>
                <p className="text-2xl font-bold text-white">{bots.length}</p>
              </div>
              <Bot className="w-8 h-8 text-primary-400" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-secondary-400 text-sm">Active Bots</p>
                <p className="text-2xl font-bold text-white">
                  {bots.filter(bot => bot.status === 'active').length}
                </p>
              </div>
              <Play className="w-8 h-8 text-green-400" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-secondary-400 text-sm">Total Conversations</p>
                <p className="text-2xl font-bold text-white">
                  {bots.reduce((sum, bot) => sum + bot.conversations, 0).toLocaleString()}
                </p>
              </div>
              <Settings className="w-8 h-8 text-accent-400" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-secondary-400 text-sm">Avg. Response Time</p>
                <p className="text-2xl font-bold text-white">1.2s</p>
              </div>
              <Settings className="w-8 h-8 text-yellow-400" />
            </div>
          </Card>
        </div>

        {/* Bots Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bots.map((bot) => (
            <Card key={bot.id} className="p-6 hover:bg-secondary-700/50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
                    <Bot className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{bot.name}</h3>
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bot.status)}`}>
                      {getStatusIcon(bot.status)}
                      <span className="capitalize">{bot.status}</span>
                    </div>
                  </div>
                </div>
                <button className="p-2 hover:bg-secondary-600 rounded-lg transition-colors">
                  <MoreVertical className="w-4 h-4 text-secondary-400" />
                </button>
              </div>

              <p className="text-secondary-300 text-sm mb-4">{bot.description}</p>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-400">Conversations:</span>
                  <span className="text-white font-medium">{bot.conversations.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-400">Last Active:</span>
                  <span className="text-white">{bot.lastActive}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-400">Created:</span>
                  <span className="text-white">{bot.createdAt}</span>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Edit}
                  className="flex-1"
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Settings}
                  className="flex-1"
                >
                  Settings
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                />
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {bots.length === 0 && (
          <Card className="p-12 text-center">
            <Bot className="w-16 h-16 text-secondary-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Bots Yet</h3>
            <p className="text-secondary-400 mb-6">
              Create your first AI chatbot to get started with automated conversations.
            </p>
            <Button
              icon={Plus}
              className="bg-primary-500 hover:bg-primary-600"
            >
              Create Your First Bot
            </Button>
          </Card>
        )}
      </div>
    </div>
  )
}

export default Bots
